import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { LayoutDashboard, Package, Gift, CreditCard, QrCode, Users, Zap, Wallet, ClipboardList, Stethoscope, ShieldCheck, BarChart3, Settings, ScrollText, ScanLine, History, UserCog, TrendingUp, Target, Contact, Receipt, Coins } from "lucide-react";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ROLE_HOME } from "@/lib/api";
import { Splash } from "@/components/common";
import StaffLayout from "@/components/StaffLayout";
import Login from "@/pages/Login";
import ChangePassword from "@/pages/ChangePassword";
import Enroll from "@/pages/Enroll";
import CustomerLayout from "@/pages/customer/CustomerLayout";
import Home from "@/pages/customer/Home";
import Explore, { ExploreDetail } from "@/pages/customer/Explore";
import MyPackages from "@/pages/customer/MyPackages";
import Cards from "@/pages/customer/Cards";
import Benefits from "@/pages/customer/Benefits";
import Book from "@/pages/customer/Book";
import Activity from "@/pages/customer/Activity";
import Profile from "@/pages/customer/Profile";
import Overview from "@/pages/admin/Overview";
import Reports from "@/pages/admin/Reports";
import Packages, { BenefitsIndex } from "@/pages/admin/Packages";
import PackageEditor from "@/pages/admin/PackageEditor";
import CardInventory from "@/pages/admin/CardInventory";
import QRCodes from "@/pages/admin/QRCodes";
import Customers from "@/pages/admin/Customers";
import { Activations, Payments, ServiceRequests } from "@/pages/admin/Operations";
import Insurance from "@/pages/admin/Insurance";
import Providers from "@/pages/admin/Providers";
import Staff from "@/pages/admin/Staff";
import SettingsPage, { AuditLog } from "@/pages/admin/SettingsPage";
import Register from "@/pages/Register";
import Success from "@/pages/Success";
import SalesDashboard from "@/pages/sales/SalesDashboard";
import NewSale from "@/pages/sales/NewSale";
import SalesList from "@/pages/sales/SalesList";
import SalesClients from "@/pages/sales/Clients";
import SalesTeam from "@/pages/sales/Team";
import SalesTargets from "@/pages/sales/Targets";
import Distributor, { DistributorAdmin } from "@/pages/sales/Distributor";
import Commission, { CommissionAdmin } from "@/pages/sales/Commission";
import { DoctorProfile, RxNew, RxList } from "@/pages/provider/Rx";
import { CouponRedeem, CouponClaims } from "@/pages/provider/Coupons";
import { PrintRx, PrintCoupon, VerifyRx } from "@/pages/PrintDoc";
import Records from "@/pages/customer/Records";
import CouponsAdmin from "@/pages/admin/CouponsAdmin";
import AskGia from "@/pages/customer/AskGia";
import Teleconsults from "@/pages/provider/Teleconsults";
import ProviderHome, { ProviderHistory } from "@/pages/provider/ProviderHome";
import ProviderRequest from "@/pages/provider/ProviderRequest";

const ADMIN_NAV = [
  { to: "/admin", label: "Overview", icon: LayoutDashboard, end: true },
  { to: "/admin/sales", label: "Sales Performance", icon: TrendingUp, end: true },
  { to: "/admin/sales/team", label: "Sales Team", icon: UserCog },
  { to: "/admin/sales/targets", label: "Sales Targets", icon: Target },
  { to: "/admin/sales/clients", label: "Sales Clients", icon: Contact },
  { to: "/admin/sales/list", label: "All Sales", icon: Receipt },
  { to: "/admin/sales/distributor", label: "Distributor Packages", icon: Package },
  { to: "/admin/sales/commission", label: "Commissions", icon: Coins },
  { to: "/admin/coupons", label: "Coupons & Claims", icon: Gift },
  { to: "/admin/packages", label: "Packages", icon: Package },
  { to: "/admin/benefits", label: "Package Benefits", icon: Gift },
  { to: "/admin/cards", label: "Card Inventory", icon: CreditCard },
  { to: "/admin/qr", label: "QR Codes", icon: QrCode },
  { to: "/admin/customers", label: "Customers", icon: Users },
  { to: "/admin/activations", label: "Activations", icon: Zap },
  { to: "/admin/payments", label: "Transactions", icon: Wallet },
  { to: "/admin/requests", label: "Service Requests", icon: ClipboardList },
  { to: "/admin/providers", label: "Service Providers", icon: Stethoscope },
  { to: "/admin/insurance", label: "Insurance", icon: ShieldCheck },
  { to: "/admin/reports", label: "Reports", icon: BarChart3 },
  { to: "/admin/staff", label: "Staff Accounts", icon: UserCog },
  { to: "/admin/audit", label: "Audit Log", icon: ScrollText },
  { to: "/admin/settings", label: "Settings", icon: Settings },
];
const SALES_NAV = [
  { to: "/sales", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/sales/new", label: "New Sale", icon: ScanLine },
  { to: "/sales/list", label: "Sales", icon: History },
  { to: "/sales/clients", label: "Clients", icon: Contact },
  { to: "/sales/distributor", label: "Distributor", icon: Package },
  { to: "/sales/commission", label: "Commission", icon: Coins },
];
const MANAGER_NAV = [...SALES_NAV, { to: "/sales/team", label: "My Team", icon: Users }, { to: "/sales/targets", label: "Targets", icon: Target }];

function SalesPortal() {
  const { user } = useAuth();
  return <StaffLayout items={user?.role === "sales_manager" ? MANAGER_NAV : SALES_NAV} portal="Sales Force Portal" />;
}
const PROVIDER_NAV = [
  { to: "/provider", label: "Request Queue", icon: ClipboardList, end: true },
  { to: "/provider/history", label: "Service History", icon: History },
  { to: "/provider/tele", label: "Remote Consults", icon: Stethoscope },
  { to: "/provider/rx", label: "e-RX", icon: ScrollText },
  { to: "/provider/coupons", label: "Redeem Coupon", icon: QrCode },
  { to: "/provider/claims", label: "Coupon Claims", icon: Wallet },
  { to: "/provider/profile", label: "Doctor Profile", icon: UserCog },
];

function Guard({ roles, children }) {
  const { user } = useAuth();
  const loc = useLocation();
  if (user === null) return <Splash />;
  if (!user) return <Navigate to={`/login?next=${encodeURIComponent(loc.pathname)}`} replace />;
  if (user.must_change_password) return <Navigate to="/change-password" replace />;
  if (!roles.includes(user.role)) return <Navigate to={ROLE_HOME[user.role]} replace />;
  return children;
}

function RootRedirect() {
  const { user } = useAuth();
  if (user === null) return <Splash />;
  if (!user) return <Navigate to="/login" replace />;
  return <Navigate to={user.must_change_password ? "/change-password" : ROLE_HOME[user.role]} replace />;
}

const ADMINS = ["admin", "super_admin"];

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<RootRedirect />} />
          <Route path="/login" element={<Login />} />
          <Route path="/change-password" element={<ChangePassword />} />
          <Route path="/enroll/:slug" element={<Enroll />} />
          <Route path="/register" element={<Register />} />
          <Route path="/success" element={<Success />} />
          <Route path="/verify/rx/:number" element={<VerifyRx />} />
          <Route path="/print/rx/:number" element={<Guard roles={["provider", "customer", ...ADMINS]}><PrintRx /></Guard>} />
          <Route path="/print/coupon/:code" element={<Guard roles={["provider", "customer", ...ADMINS]}><PrintCoupon /></Guard>} />
          <Route path="/app" element={<Guard roles={["customer"]}><CustomerLayout /></Guard>}>
            <Route index element={<Home />} />
            <Route path="explore" element={<Explore />} />
            <Route path="explore/:slug" element={<ExploreDetail />} />
            <Route path="packages" element={<MyPackages />} />
            <Route path="cards" element={<Cards />} />
            <Route path="benefits" element={<Benefits />} />
            <Route path="book" element={<Book />} />
            <Route path="activity" element={<Activity />} />
            <Route path="profile" element={<Profile />} />
            <Route path="records" element={<Records />} />
            <Route path="gia" element={<AskGia />} />
          </Route>
          <Route path="/admin" element={<Guard roles={ADMINS}><StaffLayout items={ADMIN_NAV} portal="Admin Portal" /></Guard>}>
            <Route index element={<Overview />} />
            <Route path="packages" element={<Packages />} />
            <Route path="packages/:id" element={<PackageEditor />} />
            <Route path="benefits" element={<BenefitsIndex />} />
            <Route path="cards" element={<CardInventory />} />
            <Route path="qr" element={<QRCodes />} />
            <Route path="customers" element={<Customers />} />
            <Route path="activations" element={<Activations />} />
            <Route path="payments" element={<Payments />} />
            <Route path="requests" element={<ServiceRequests />} />
            <Route path="providers" element={<Providers />} />
            <Route path="insurance" element={<Insurance />} />
            <Route path="reports" element={<Reports />} />
            <Route path="staff" element={<Staff />} />
            <Route path="sales" element={<SalesDashboard />} />
            <Route path="sales/team" element={<SalesTeam />} />
            <Route path="sales/targets" element={<SalesTargets />} />
            <Route path="sales/clients" element={<SalesClients />} />
            <Route path="sales/list" element={<SalesList />} />
            <Route path="sales/distributor" element={<DistributorAdmin />} />
            <Route path="sales/commission" element={<CommissionAdmin />} />
            <Route path="coupons" element={<CouponsAdmin />} />
            <Route path="audit" element={<AuditLog />} />
            <Route path="settings" element={<SettingsPage />} />
          </Route>
          <Route path="/cashier/*" element={<Navigate to="/sales" replace />} />
          <Route path="/sales" element={<Guard roles={["sales_manager", "sales_agent"]}><SalesPortal /></Guard>}>
            <Route index element={<SalesDashboard />} />
            <Route path="new" element={<NewSale />} />
            <Route path="list" element={<SalesList />} />
            <Route path="clients" element={<SalesClients />} />
            <Route path="distributor" element={<Distributor />} />
            <Route path="commission" element={<Commission />} />
            <Route path="team" element={<Guard roles={["sales_manager"]}><SalesTeam /></Guard>} />
            <Route path="targets" element={<Guard roles={["sales_manager"]}><SalesTargets /></Guard>} />
          </Route>
          <Route path="/provider" element={<Guard roles={["provider"]}><StaffLayout items={PROVIDER_NAV} portal="Provider Portal" /></Guard>}>
            <Route index element={<ProviderHome />} />
            <Route path="requests/:id" element={<ProviderRequest />} />
            <Route path="history" element={<ProviderHistory />} />
            <Route path="rx" element={<RxList />} />
            <Route path="tele" element={<Teleconsults />} />
            <Route path="rx/new" element={<RxNew />} />
            <Route path="coupons" element={<CouponRedeem />} />
            <Route path="claims" element={<CouponClaims />} />
            <Route path="profile" element={<DoctorProfile />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-center" richColors />
    </AuthProvider>
  );
}
